import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';
import { TenantSchedule } from 'src/app/shared/models/tenant-schedule';
import { SaveResponse } from '../../models/save-response';
import { TenantScheduleRequest } from '../../models/tenant-schedule-request';
import { TenantScheduleResponse } from '../../models/tenant-schedule-response';
import { TenantApiService } from '../http/tenant-api.service';
import AutoMapperService from './auto-mapper.service';
import { TenantScheduleSummaryResponse } from '../../models/tenant-schedule-summary-response';
import { TenantScheduleSummary } from 'src/app/shared/models/tenant-schedule-summary';
import { TenantScheduleVM } from 'src/app/modules/platform/schedules/models/tenant-schedule-vm.model';

@Injectable({
    providedIn: 'root'
})
export class TenantScheduleService {
    constructor(private api: TenantApiService) {}

    private cachedExpressions: { [key: string]: boolean } | null = null;
    private inFlightExpressionRequests: { [key: string]: Observable<boolean> } = {};

    validateCron(expression: string): Observable<boolean> {
        if (this.cachedExpressions && Object.prototype.hasOwnProperty.call(this.cachedExpressions, expression)) {
            return of(this.cachedExpressions[expression]);
        }

        // Multiple controls can validate the same cron expression concurrently.
        // Reuse the in-flight request so we only issue one HTTP call per expression.
        // This happens when a control is first initialized.
        if (this.inFlightExpressionRequests[expression]) {
            return this.inFlightExpressionRequests[expression];
        }

        const request$ = this.api.validateCron(expression).pipe(
            map(response => response.isValid === true),
            tap(isValid => {
                if (!this.cachedExpressions) {
                    this.cachedExpressions = {};
                }
                this.cachedExpressions[expression] = isValid;
            }),
            catchError(() => of(false)),
            tap(() => delete this.inFlightExpressionRequests[expression]),
            shareReplay(1)
        );

        this.inFlightExpressionRequests[expression] = request$;
        return request$;
    }

    getTenantSchedules(tenantId: string): Observable<TenantSchedule[]> {
        return this.api.getTenantSchedules(tenantId).pipe(
            map(response => {
                return (
                    response?.map(data =>
                        AutoMapperService.mapToApplicationModel<TenantScheduleResponse, TenantSchedule>(data)
                    ) ?? []
                );
            })
        );
    }

    getTenantScheduleSummaries(tenantId: string) {
        return this.api.getTenantScheduleSummaries(tenantId).pipe(
            map(response => {
                const schedules =
                    response?.map(data =>
                        AutoMapperService.mapToApplicationModel<TenantScheduleSummaryResponse, TenantScheduleSummary>(
                            data
                        )
                    ) ?? [];

                schedules.map(schedule =>
                    AutoMapperService.convertStringsToDates(
                        schedule,
                        'ingestionJobMaxLastUtc',
                        'ingestionJobMinNextUtc'
                    )
                );
                return schedules;
            })
        );
    }

    getTenantSchedule(tenantId: string, scheduleId: number): Observable<TenantScheduleSummary> {
        return this.api.getTenantSchedule(tenantId, scheduleId);
    }

    saveTenantSchedule(tenantSchedule: TenantSchedule): Observable<SaveResponse> {
        const request = this.prepScheduleUpsertRequest(tenantSchedule);
        return this.api.saveTenantSchedule(request);
    }

    deleteSingleTenantSchedule(tenantId: string, scheduleId: number): Observable<object> {
        return this.api.deleteSingleTenantSchedule(tenantId, scheduleId);
    }

    deleteMultipleTenantSchedules(tenantId: string, scheduleIds: number[]): Observable<object> {
        return this.api.deleteMultipleTenantSchedules(tenantId, scheduleIds);
    }

    updateTenantSchedule(tenantSchedule: TenantSchedule): Observable<SaveResponse> {
        const request = this.prepScheduleUpsertRequest(tenantSchedule);
        return this.api.updateTenantSchedule(request);
    }

    queueTenantScheduleJobs(tenantId: string, scheduleId: number) {
        return this.api.queueTenantScheduleJobs(tenantId, scheduleId);
    }

    bulkQueueTenantScheduleJobs(tenantId: string, ingestionIds: number[]) {
        return this.api.bulkQueueTenantScheduleJobs(tenantId, ingestionIds);
    }

    toggleActiveFlag(schedule: TenantScheduleVM, tenantId: string, oktaUserName: string): Observable<SaveResponse> {
        const tenantScheduleRequest = <TenantSchedule>{};
        tenantScheduleRequest.tenantId = tenantId;
        tenantScheduleRequest.description = schedule.description;
        tenantScheduleRequest.cron = schedule.cron;
        tenantScheduleRequest.activeFlag = !schedule.activeFlag;
        tenantScheduleRequest.updatedUser = oktaUserName;
        tenantScheduleRequest.tenantScheduleId = schedule.tenantScheduleId;

        return this.updateTenantSchedule(tenantScheduleRequest);
    }

    private prepScheduleUpsertRequest(tenantSchedule: TenantSchedule): TenantScheduleRequest {
        return AutoMapperService.mapToApiModel<TenantSchedule, TenantScheduleRequest>(tenantSchedule);
    }
}
